import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, Lock, Save, Shield } from 'lucide-react';

import { PageLayout } from '@/components/Layout/PageLayout';
import AccessDenied from '@/components/Common/AccessDenied';
import { Card, CardContent } from '@/components/Common/Card';
import { Button } from '@/components/Common/button';
import { Badge } from '@/components/Common/badge';
import { Input } from '@/components/Common/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/Common/tabs';
import { ProfilePermissionMatrix } from '@/components/Admin/ProfilePermissionMatrix';
import {
  ProfileNavigationEditor,
  type ExperienceChoice,
  type InheritedNavigation,
} from '@/components/Admin/ProfileNavigationEditor';
import {
  ProfileDashboardEditor,
  type DashboardWidgetChoice,
} from '@/components/Admin/ProfileDashboardEditor';
import { ProfileUsersPanel } from '@/components/Admin/ProfileUsersPanel';
import { useEffectiveUser, useIsSuperAdmin } from '@/stores/authStore';
import { profileService } from '@/services/profileService';
import organizationService from '@/services/organizationService';
import { ORG_LANDING_PAGE } from '@/hooks/useLandingPath';
import { notify } from '@/lib/notify';
import {
  DASHBOARD_WIDGET_IDS,
  NAV_SECTION_IDS,
  PROJECT_DASHBOARD_WIDGETS,
  applyOrder,
  navSectionItems,
} from '@/lib/profileUi';
import type {
  ControlledResource,
  DefaultForRole,
  LandingPage,
  NavItemId,
  NavSection,
  Profile,
  ProfilePermission,
  ProfileUiConfig,
  ProfileUser,
} from '@/types/profiles';

const TABS = ['overview', 'permissions', 'navigation', 'dashboard', 'users'] as const;
type ProfileTab = (typeof TABS)[number];

const KEY_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

type Scope = 'global' | 'org';
type NavOrder = Record<NavSection, NavItemId[]>;

interface FormState {
  name: string;
  key: string;
  description: string;
  scope: Scope;
  defaultForRole: DefaultForRole | '';
  landingPage: LandingPage | '';
  permissions: ProfilePermission[];
  experience: ExperienceChoice;
  hidden: NavItemId[];
  navOrder: NavOrder;
  dashboardDefault: boolean;
  widgets: DashboardWidgetChoice[];
}

const toKey = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .slice(0, 64);

const slugify = (s: string) => toKey(s.trim()).replace(/^[-_]+|-+$/g, '');

const navOrderFrom = (order: Partial<NavOrder>): NavOrder =>
  Object.fromEntries(
    NAV_SECTION_IDS.map((s) => [s, applyOrder(navSectionItems(s), (id) => id, order[s])])
  ) as NavOrder;

const defaultWidgets = (): DashboardWidgetChoice[] =>
  DASHBOARD_WIDGET_IDS.map((id) => ({ id, visible: !PROJECT_DASHBOARD_WIDGETS.includes(id) }));

function formFromProfile(profile: Profile | null): FormState {
  const ui = profile?.ui_config ?? null;
  const order: Partial<NavOrder> = ui?.navigation?.order ?? {};
  const configured = ui?.dashboard?.widgets ?? null;
  const visibleById = new Map((configured ?? []).map((w) => [w.id, w.visible !== false] as const));

  return {
    name: profile?.name ?? '',
    key: profile?.key ?? '',
    description: profile?.description ?? '',
    scope: profile?.organization_id ? 'org' : 'global',
    defaultForRole: profile?.default_for_role ?? '',
    landingPage: profile?.landing_page ?? '',
    permissions: profile?.permissions ?? [],
    experience: ui?.experience ?? '',
    hidden: ui?.navigation?.hidden ?? [],
    navOrder: navOrderFrom(order),
    dashboardDefault: configured === null,
    widgets: configured
      ? applyOrder(
          DASHBOARD_WIDGET_IDS,
          (id) => id,
          configured.map((w) => w.id)
        ).map((id) => ({ id, visible: visibleById.get(id) ?? false }))
      : defaultWidgets(),
  };
}

/** ui_config to send; null when every part is default/inherit (plan §9.4). */
function buildUiConfig(form: FormState): ProfileUiConfig | null {
  const defaults = navOrderFrom({});
  const order: Partial<NavOrder> = {};
  for (const section of NAV_SECTION_IDS) {
    if (form.navOrder[section].join('|') !== defaults[section].join('|')) {
      order[section] = form.navOrder[section];
    }
  }
  const hasOrder = Object.keys(order).length > 0;
  const navigation =
    form.hidden.length > 0 || hasOrder
      ? {
          ...(form.hidden.length > 0 ? { hidden: form.hidden } : {}),
          ...(hasOrder ? { order } : {}),
        }
      : null;
  const dashboard = form.dashboardDefault
    ? null
    : { widgets: form.widgets.map((w) => ({ id: w.id, visible: w.visible })) };
  const experience = form.experience || null;

  if (!experience && !navigation && !dashboard) return null;
  return { experience, navigation, dashboard };
}

const errorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : err?.message ?? 'Unknown error';
};

const isTab = (value: string | undefined): value is ProfileTab =>
  !!value && (TABS as readonly string[]).includes(value);

/**
 * Tabbed profile editor (plan §9.2): /admin/profiles/new/:tab? and
 * /admin/profiles/:profileId/:tab?. Replaces the former ProfileFormModal.
 */
const ProfileEditPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { profileId, tab } = useParams<{ profileId?: string; tab?: string }>();
  const isCreate = !profileId;

  const isSuperAdmin = useIsSuperAdmin();
  const effectiveUser = useEffectiveUser();
  const ownOrgId = effectiveUser?.organization_id ?? null;
  const canCreate = isSuperAdmin;

  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(!isCreate);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(() => formFromProfile(null));
  const [initialForm, setInitialForm] = useState<FormState>(() => formFromProfile(null));
  const [keyTouched, setKeyTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<ProfileUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const availableTabs: readonly ProfileTab[] = isCreate ? TABS.filter((x) => x !== 'users') : TABS;
  const activeTab: ProfileTab = isTab(tab) && availableTabs.includes(tab) ? tab : 'overview';
  const basePath = isCreate ? '/admin/profiles/new' : `/admin/profiles/${profileId}`;

  // Users are listed in the profile's org; a global profile shows the admin's own org.
  const usersOrgId = profile?.organization_id ?? ownOrgId;

  // An org profile inherits its org's landing page and experience; show them in the
  // Navigation tab. A global profile spans orgs, so it has no single value to show.
  const inheritOrgId = form.scope === 'org' ? (profile?.organization_id ?? ownOrgId) : null;
  const [inherited, setInherited] = useState<InheritedNavigation | null>(null);
  useEffect(() => {
    setInherited(null);
    if (!inheritOrgId) return;
    let cancelled = false;
    organizationService
      .getUiSettings(inheritOrgId)
      .then((settings) => {
        if (cancelled) return;
        setInherited({
          landing: settings.landing ? ORG_LANDING_PAGE[settings.landing] : null,
          experience: settings.experience,
        });
      })
      .catch((err: unknown) => console.error('Error loading the organization UI settings:', err));
    return () => {
      cancelled = true;
    };
  }, [inheritOrgId]);

  useEffect(() => {
    setError(null);
    if (!profileId) {
      const empty = formFromProfile(null);
      setProfile(null);
      setForm(empty);
      setInitialForm(empty);
      setKeyTouched(false);
      setLoadError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    profileService
      .get(profileId)
      .then((p) => {
        if (cancelled) return;
        const next = formFromProfile(p);
        setProfile(p);
        setForm(next);
        setInitialForm(next);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(errorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const loadUsers = useCallback(async () => {
    if (!profileId) return;
    setUsersLoading(true);
    try {
      // Only super-admins may target another organization.
      setUsers(await profileService.users(profileId, isSuperAdmin ? usersOrgId : undefined));
    } catch (e) {
      setUsers([]);
      notify.error(errorMessage(e));
    } finally {
      setUsersLoading(false);
    }
  }, [profileId, isSuperAdmin, usersOrgId]);

  const loadedProfileId = profile?.id ?? null;
  useEffect(() => {
    if (loadedProfileId) void loadUsers();
  }, [loadedProfileId, loadUsers]);

  // Global/system profiles are read-only for org admins; super-admins may edit
  // system profiles (same rule as the former modal and the list page).
  const readOnly = !isCreate && !!profile && !(isSuperAdmin || profile.organization_id !== null);

  // `default_for_role` only applies to org-scoped profiles. Org admins always
  // create org-scoped profiles (server forces their org); super-admins choose.
  const isOrgScoped = isCreate
    ? !isSuperAdmin || form.scope === 'org'
    : profile?.organization_id != null;

  const dirty = useMemo(
    () => isCreate || JSON.stringify(form) !== JSON.stringify(initialForm),
    [isCreate, form, initialForm]
  );

  const canReadResource = useCallback(
    (resource: ControlledResource) =>
      form.permissions.find((p) => p.resource === resource)?.read ?? false,
    [form.permissions]
  );

  const patch = (partial: Partial<FormState>) => setForm((f) => ({ ...f, ...partial }));

  const goToTab = (next: string) => {
    if (isTab(next)) navigate(`${basePath}/${next}`, { replace: true });
  };

  const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleString(i18n.language) : '—';

  const handleDuplicate = async () => {
    if (!profile) return;
    setDuplicating(true);
    try {
      const copy = await profileService.duplicate(profile.id, {
        name: t('profiles.duplicate.copyOf', { name: profile.name }),
      });
      notify.success(t('profiles.duplicate.done', 'Profile duplicated'));
      navigate(`/admin/profiles/${copy.id}/overview`);
    } catch (e) {
      notify.error(errorMessage(e));
    } finally {
      setDuplicating(false);
    }
  };

  const handleSave = async () => {
    if (readOnly) return;
    if (!form.name.trim()) {
      setError(t('profiles.form.nameRequired', 'Name is required'));
      goToTab('overview');
      return;
    }
    if (isCreate && !form.key.trim()) {
      setError(t('profiles.form.keyRequired', 'Key is required'));
      goToTab('overview');
      return;
    }
    if (isCreate && !KEY_PATTERN.test(form.key)) {
      setError(
        t(
          'profiles.form.keyInvalid',
          'Use lowercase letters, digits, "-" or "_", starting with a letter or a digit.'
        )
      );
      goToTab('overview');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      // Explicit null clears default_for_role server-side; omit entirely for
      // global/system profiles where the field is invalid.
      const defaultForRolePayload = isOrgScoped ? { default_for_role: form.defaultForRole || null } : {};
      const common = {
        name: form.name.trim(),
        description: form.description || null,
        permissions: form.permissions,
        // Empty selection clears the landing page (null → server default).
        landing_page: form.landingPage || null,
        ui_config: buildUiConfig(form),
        ...defaultForRolePayload,
      };

      if (isCreate) {
        const created = await profileService.create({
          key: form.key.trim(),
          ...common,
          ...(isSuperAdmin && form.scope === 'org' && ownOrgId ? { organization_id: ownOrgId } : {}),
        });
        notify.success(t('profiles.created', 'Profile created'));
        navigate(`/admin/profiles/${created.id}/${activeTab}`, { replace: true });
      } else if (profile) {
        const updated = await profileService.update(profile.id, common);
        const next = formFromProfile(updated);
        setProfile(updated);
        setForm(next);
        setInitialForm(next);
        notify.success(t('profiles.updated', 'Profile updated'));
      }
    } catch (e) {
      const message = errorMessage(e);
      setError(message);
      notify.error(message);
    } finally {
      setSaving(false);
    }
  };

  const pageIcon = <Shield className="h-6 w-6" />;

  if (!isSuperAdmin) {
    return <AccessDenied />;
  }

  const backButton = (
    <Button variant="ghost" onClick={() => navigate('/admin/profiles')}>
      <ArrowLeft className="h-4 w-4 mr-1" />
      {t('profiles.editor.back', 'Back to profiles')}
    </Button>
  );

  if (loading || loadError) {
    return (
      <PageLayout icon={pageIcon} title={t('profiles.title', 'Profiles')} subtitleExtra={backButton}>
        <div className={loadError ? 'text-destructive text-sm' : 'text-muted-foreground text-sm'}>
          {loadError ?? t('common.loading', 'Loading…')}
        </div>
      </PageLayout>
    );
  }

  const title = isCreate
    ? t('profiles.form.createTitle', 'New profile')
    : profile?.name || t('profiles.form.editTitle', 'Edit profile');

  const scopeBadge = profile?.is_system ? (
    <Badge variant="secondary">{t('profiles.system', 'system')}</Badge>
  ) : profile && profile.organization_id === null ? (
    <Badge variant="secondary">{t('profiles.global', 'global')}</Badge>
  ) : profile ? (
    <Badge variant="outline">{t('profiles.organization', 'organization')}</Badge>
  ) : null;

  return (
    <PageLayout
      icon={pageIcon}
      title={title}
      titleSuffix={scopeBadge}
      subtitleExtra={backButton}
      headerRight={
        <div className="flex items-center gap-2">
          {!isCreate && canCreate && (
            <Button variant="outline" onClick={handleDuplicate} disabled={duplicating}>
              <Copy className="h-4 w-4 mr-1" />
              {t('profiles.duplicate.action', 'Duplicate')}
            </Button>
          )}
          {!readOnly && (
            <Button onClick={handleSave} disabled={saving || !dirty}>
              <Save className="h-4 w-4 mr-1" />
              {saving
                ? t('common.saving', 'Saving…')
                : isCreate
                ? t('common.create', 'Create')
                : t('common.save', 'Save')}
            </Button>
          )}
        </div>
      }
    >
      {readOnly && (
        <div
          role="status"
          className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-md border border-border bg-muted/40 p-3 text-sm"
        >
          <Lock className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="flex-1">
            {profile?.is_system
              ? t('profiles.editor.readOnlySystem', 'System profile — duplicate it to customise.')
              : t(
                  'profiles.editor.readOnlyGlobal',
                  'Global profile — read-only for organization admins. Duplicate it to customise.'
                )}
          </span>
          {canCreate && (
            <Button size="sm" variant="outline" onClick={handleDuplicate} disabled={duplicating}>
              <Copy className="h-4 w-4 mr-1" />
              {t('profiles.duplicate.action', 'Duplicate')}
            </Button>
          )}
        </div>
      )}

      {!readOnly && profile?.is_system && (
        <p className="text-xs text-muted-foreground">
          {t('profiles.form.systemEditable', '(system profile — editable by super-admin)')}
        </p>
      )}

      {error && (
        <div
          role="alert"
          className="text-sm text-destructive border border-destructive/40 bg-destructive/10 rounded-md p-2"
        >
          {error}
        </div>
      )}

      <Tabs defaultValue="overview" value={activeTab} onValueChange={goToTab}>
        <TabsList className="flex-wrap h-auto">
          {availableTabs.map((x) => (
            <TabsTrigger key={x} value={x}>
              {t(`profiles.editor.tabs.${x}`)}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block" htmlFor="profile-name">
                    {t('profiles.form.name', 'Name')}
                  </label>
                  <Input
                    id="profile-name"
                    value={form.name}
                    disabled={readOnly}
                    onChange={(e) => {
                      const name = e.target.value;
                      setForm((f) => ({
                        ...f,
                        name,
                        ...(isCreate && !keyTouched ? { key: slugify(name) } : {}),
                      }));
                    }}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" htmlFor="profile-key">
                    {t('profiles.form.key', 'Key')}
                  </label>
                  <Input
                    id="profile-key"
                    value={form.key}
                    disabled={!isCreate}
                    className="font-mono"
                    onChange={(e) => {
                      setKeyTouched(true);
                      patch({ key: toKey(e.target.value) });
                    }}
                    placeholder="e.g. audit-readonly"
                    aria-invalid={isCreate && form.key !== '' && !KEY_PATTERN.test(form.key)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('profiles.form.keyHelp', 'Unique, lowercase slug. Immutable after creation.')}
                  </p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block" htmlFor="profile-description">
                  {t('profiles.form.description', 'Description')}
                </label>
                <textarea
                  id="profile-description"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  rows={2}
                  value={form.description}
                  disabled={readOnly}
                  onChange={(e) => patch({ description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block" htmlFor="profile-scope">
                    {t('profiles.editor.scope', 'Scope')}
                  </label>
                  {isCreate && isSuperAdmin ? (
                    <select
                      id="profile-scope"
                      className="w-full border border-input rounded-md bg-background px-3 py-2 text-sm"
                      value={form.scope}
                      onChange={(e) => patch({ scope: e.target.value as Scope })}
                    >
                      <option value="global">{t('profiles.editor.scopeGlobal', 'Global (all organizations)')}</option>
                      {ownOrgId && (
                        <option value="org">{t('profiles.editor.scopeOrg', 'My organization')}</option>
                      )}
                    </select>
                  ) : (
                    <div id="profile-scope" className="text-sm py-2">
                      {isOrgScoped
                        ? t('profiles.editor.scopeOrg', 'My organization')
                        : t('profiles.editor.scopeGlobal', 'Global (all organizations)')}
                    </div>
                  )}
                </div>

                {isOrgScoped && (
                  <div>
                    <label className="text-sm font-medium mb-1 block" htmlFor="profile-default-role">
                      {t('profiles.form.defaultForRole', 'Default profile for role')}
                    </label>
                    <select
                      id="profile-default-role"
                      className="w-full border border-input rounded-md bg-background px-3 py-2 text-sm"
                      value={form.defaultForRole}
                      disabled={readOnly}
                      onChange={(e) => patch({ defaultForRole: e.target.value as DefaultForRole | '' })}
                    >
                      <option value="">{t('profiles.form.defaultForRoleNone', '— none —')}</option>
                      <option value="admin">{t('profiles.form.roleAdmin', 'Admin')}</option>
                      <option value="user">{t('profiles.form.roleUser', 'User')}</option>
                      {form.defaultForRole === 'demo' && (
                        <option value="demo">{t('profiles.form.roleDemo', 'Demo')}</option>
                      )}
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t(
                        'profiles.form.defaultForRoleHelp',
                        'Automatically assigned to new members of the organization with this role.'
                      )}
                    </p>
                  </div>
                )}
              </div>

              {!isCreate && profile && (
                <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-border/60 pt-4 text-sm">
                  <div>
                    <dt className="text-xs text-muted-foreground">{t('profiles.editor.usersCount', 'Members')}</dt>
                    <dd className="font-medium">{usersLoading ? '…' : users.length}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">{t('profiles.editor.createdAt', 'Created')}</dt>
                    <dd>{formatDate(profile.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">{t('profiles.editor.updatedAt', 'Last modified')}</dt>
                    <dd>{formatDate(profile.updated_at)}</dd>
                  </div>
                </dl>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="permissions">
          <Card>
            <CardContent className="pt-6">
              <ProfilePermissionMatrix
                permissions={form.permissions}
                onChange={(permissions) => patch({ permissions })}
                readOnly={readOnly}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="navigation">
          <Card>
            <CardContent className="pt-6">
              <ProfileNavigationEditor
                landingPage={form.landingPage}
                onLandingPageChange={(landingPage) => patch({ landingPage })}
                experience={form.experience}
                onExperienceChange={(experience) => patch({ experience })}
                hidden={form.hidden}
                onHiddenChange={(hidden) => patch({ hidden })}
                order={form.navOrder}
                onOrderChange={(navOrder) => patch({ navOrder })}
                canRead={canReadResource}
                inherited={inherited}
                readOnly={readOnly}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dashboard">
          <Card>
            <CardContent className="pt-6">
              <ProfileDashboardEditor
                useDefault={form.dashboardDefault}
                onUseDefaultChange={(dashboardDefault) => patch({ dashboardDefault })}
                widgets={form.widgets}
                onWidgetsChange={(widgets) => patch({ widgets })}
                canRead={canReadResource}
                experience={form.experience}
                readOnly={readOnly}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {!isCreate && profile && (
          <TabsContent value="users">
            <Card>
              <CardContent className="pt-6">
                <ProfileUsersPanel
                  profileId={profile.id}
                  organizationId={usersOrgId}
                  users={users}
                  loading={usersLoading}
                  onChanged={loadUsers}
                />
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </PageLayout>
  );
};

export default ProfileEditPage;
